import { useEffect, useState } from 'react';
import { Box, Text, Stack, Button, Group } from '@mantine/core';
import { fieldKey, missingRequiredFields } from '@holloway/dsl';
import { IconArrowRight } from '@tabler/icons-react';
import { renderRichTextWithVars, VAR_BUBBLE_CSS } from '@/features/editor/utils/renderRichText';
import { NAVY } from './styles';
import { BackButton } from './BackButton';
import { PageElementRenderer } from './PageElementRenderer';

interface PageNodeRendererProps {
  title: string;
  content: string;
  elements: any[];
  input: Record<string, any>;
  onInputChange: (input: Record<string, any>) => void;
  onAdvance: (branchTarget?: string) => void;
  onBack?: () => void;
  isAdvancing?: boolean;
  sessionState?: Record<string, any>;
  sessionId?: string | null;
  mode?: 'preview' | 'live';
}

export function PageNodeRenderer({ title, content, elements, input, onInputChange, onAdvance, onBack, isAdvancing = false, sessionState = {}, sessionId, mode }: PageNodeRendererProps) {
  // Answers are kept under fieldKey: the bound variable, or the element id
  // when none is bound. Unbound answers are not stored, but "Required" still
  // has to see them (the engine reads the same keys).
  const handleElementChange = (element: any, value: any) => {
    onInputChange({ ...input, [fieldKey(element)]: value });
  };

  // Required fields are checked on every way off the page (Next Step, a
  // branch option, a button). Until the viewer tries to leave, nothing is
  // flagged; after that, each empty required field says so until filled.
  const [triedToLeave, setTriedToLeave] = useState(false);
  useEffect(() => setTriedToLeave(false), [elements]);
  const missingIds = new Set(triedToLeave ? missingRequiredFields({ elements }, input).map((f) => f.elementId) : []);

  const canLeave = (withInput: Record<string, any>) => {
    if (missingRequiredFields({ elements }, withInput).length === 0) return true;
    setTriedToLeave(true);
    return false;
  };

  const handleBranchSelect = (targetPageId: string, element: any, value: any) => {
    const next = { ...input, [fieldKey(element)]: value, _branchTarget: targetPageId };
    onInputChange(next);
    if (!canLeave(next)) return;
    setTimeout(() => onAdvance(targetPageId), 50);
  };

  const handleAdvance = () => {
    if (!canLeave(input)) return;
    setTimeout(() => onAdvance(), 50);
  };

  // Button elements and branching elements (YesNo/MultiChoice with createBranches)
  // supply the page's forward navigation. A branching page is saved with no `next`
  // (see useFlowPersistence), so a plain Next Step would advance to nothing.
  const hasOwnNavigation = elements.some((el: any) => el.type === 'Button' || el.createBranches === true);

  return (
    <Box>
      <Text size="xl" fw={700} mb={4} c="#2A3140">
        {title}
      </Text>
      {content && (
        <>
          <style>{VAR_BUBBLE_CSS}</style>
          <div
            className="rich-text-render"
            dangerouslySetInnerHTML={{ __html: renderRichTextWithVars(content, sessionState) }}
            style={{ fontSize: '0.875rem', color: '#4E5A6B', lineHeight: 1.6, marginBottom: 16 }}
          />
        </>
      )}

      <Stack gap="md">
        {elements.map((element: any) => (
        <PageElementRenderer
            key={element.id}
            element={element}
            value={input[fieldKey(element)]}
            onChange={(value) => handleElementChange(element, value)}
            onBranchSelect={(targetPageId, value) => handleBranchSelect(targetPageId, element, value)}
            onAdvance={handleAdvance}
            error={missingIds.has(element.id) ? 'This field is required.' : undefined}
            sessionState={sessionState}
            sessionId={sessionId}
            mode={mode}
          />
        ))}
      </Stack>

      {/* A page with its own navigation (buttons or branches) supplies the way
          forward, but nothing supplied a way back — so Back is rendered either way,
          beside Next Step when the page has no navigation of its own and alone when
          it does. Skipped when it would be empty: such a page, on the first step,
          has nothing to put here. */}
      {(onBack || !hasOwnNavigation) && (
        <Group justify="space-between" mt="xl">
          <BackButton onBack={onBack} isAdvancing={isAdvancing} />
          {!hasOwnNavigation && (
            <Button
              rightSection={<IconArrowRight size={16} />}
              onClick={() => {
                if (canLeave(input)) onAdvance();
              }}
              loading={isAdvancing}
              style={{ backgroundColor: NAVY }}
              radius="md"
              size="md"
            >
              Next Step
            </Button>
          )}
        </Group>
      )}
    </Box>
  );
}
